import { ProjectRepresentative, ReportMetadata, VorankuendigungCoordinator } from '../types';
import {
    getAusfuehrungsVertreter,
    getPlanungsVerantwortliche,
    getRepresentatives,
} from './projectRepresentatives';
import { emptyCoordinator, filledCoordinators } from './vorankuendigungModel';

/**
 * Punkt 5 der Vorankündigung (Koordinatoren a/b) aus den Projekt-Stammdaten.
 *
 * Entscheid Betreiber 12.10. (docs/projekt-kickoff-plan.md §5):
 *   - Gibt es Verantwortliche Planungsphase, stehen SIE unter 5a (Planung);
 *     unter 5b (Ausführung) stehen der Hauptverantwortliche und seine
 *     Ausführungs-Vertreter.
 *   - Gibt es keine, bleibt alles wie bisher: 5a nur mit Haken „Eigene Firma",
 *     beide Punkte aus der ganzen Vertreterliste.
 *
 * Die Regel des Abgleichs bleibt unverändert: Eine Liste wird nur befüllt,
 * solange sie leer ist. Danach zieht er ausschließlich die Firmenanschrift der
 * Einträge nach, die nachweislich aus den Stammdaten stammen (`coordinatorId`).
 * Von Hand Ergänztes bleibt unangetastet — sonst löschte der nächste
 * Metadaten-Wechsel zusätzliche Personen wieder weg.
 *
 * Reine Funktionen, ohne DOM — die Ansicht ruft sie im Abgleich-Effekt auf.
 */

export interface KoordinatorQuellen {
    /** Quelle für 5a. */
    planung: ProjectRepresentative[];
    /** Quelle für 5b. */
    ausfuehrung: ProjectRepresentative[];
    /** Gibt es eigene Verantwortliche Planungsphase im Projekt? */
    mitPlanungsVerantwortlichen: boolean;
}

/** Wer gehört nach 5a, wer nach 5b? */
export const koordinatorQuellen = (meta: ReportMetadata | undefined | null): KoordinatorQuellen => {
    const planung = getPlanungsVerantwortliche(meta);
    if (planung.length > 0) {
        return { planung, ausfuehrung: getAusfuehrungsVertreter(meta), mitPlanungsVerantwortlichen: true };
    }
    // Ohne Planungsperson exakt der bisherige Weg: beide Punkte aus derselben
    // Liste, Index 0 = Koordinator, alle weiteren als Vertreter.
    const alle = getRepresentatives(meta);
    return { planung: alle, ausfuehrung: alle, mitPlanungsVerantwortlichen: false };
};

/**
 * Vertreterliste → Einträge für Punkt 5. Die erste Person ist die
 * Koordinatorin (ohne Rolle, im Editor „Koordinator/in"), alle weiteren
 * tragen die Rolle „Vertreter" — dieselbe Vorgabe, die der Editor beim
 * Hinzufügen einer weiteren Person setzt.
 */
export const koordinatorenAusVertretern = (
    reps: ProjectRepresentative[],
    companyString: string,
): VorankuendigungCoordinator[] =>
    reps
        .filter(rep => (rep.name || '').trim())
        .map((rep, index) => ({
            ...emptyCoordinator(index === 0 ? undefined : 'Vertreter'),
            company: companyString,
            name: rep.name,
            phone: rep.phone || '',
            email: rep.email || '',
            coordinatorId: rep.id,
        }));

/**
 * Gleicht EINE Liste ab. `null` heißt: nichts zu ändern.
 *
 * Leer → aus den Stammdaten befüllen. Sonst nur die Firmenanschrift der
 * Stammdaten-Einträge angleichen, nie Personen ergänzen, ersetzen oder
 * entfernen.
 */
export const gleicheKoordinatorenAb = (
    list: VorankuendigungCoordinator[] | undefined,
    quelle: ProjectRepresentative[],
    companyString: string,
): VorankuendigungCoordinator[] | null => {
    const current = list || [];
    if (filledCoordinators(current).length === 0) {
        const fresh = koordinatorenAusVertretern(quelle, companyString);
        return fresh.length ? fresh : null;
    }
    if (!companyString) return null;
    let touched = false;
    const next = current.map(entry => {
        if (!entry.coordinatorId || entry.company === companyString) return entry;
        touched = true;
        return { ...entry, company: companyString };
    });
    return touched ? next : null;
};

export interface KoordinatorAbgleich {
    planningCoordinators?: VorankuendigungCoordinator[];
    executionCoordinators?: VorankuendigungCoordinator[];
}

/**
 * Abgleich von 5a und 5b in einem Schritt. Liefert nur die Listen, die sich
 * ändern.
 *
 * 5a wird abgeglichen, wenn der Anwender „Eigene Firma" gewählt hat — wie
 * bisher — ODER wenn das Projekt eigene Verantwortliche Planungsphase führt.
 * Die sind Kollegen der eigenen Firma; dass die Planung extern liegt, schließt
 * ihre Eintragung in den Stammdaten aus. Weil auch dann nur eine LEERE Liste
 * befüllt wird, bleibt eine von Hand eingetragene fremde Planungskoordination
 * stehen.
 */
export const gleichePunktFuenfAb = (
    data: {
        useOwnCompanyForPlanning?: boolean;
        planningCoordinators?: VorankuendigungCoordinator[];
        executionCoordinators?: VorankuendigungCoordinator[];
    },
    meta: ReportMetadata | undefined | null,
    companyString: string,
): KoordinatorAbgleich => {
    const quellen = koordinatorQuellen(meta);
    const out: KoordinatorAbgleich = {};

    if (data.useOwnCompanyForPlanning || quellen.mitPlanungsVerantwortlichen) {
        const planning = gleicheKoordinatorenAb(data.planningCoordinators, quellen.planung, companyString);
        if (planning) out.planningCoordinators = planning;
    }

    const execution = gleicheKoordinatorenAb(data.executionCoordinators, quellen.ausfuehrung, companyString);
    if (execution) out.executionCoordinators = execution;

    return out;
};
